import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, MapPin } from "lucide-react";
import { Footer } from "../components/Footer";
import { LanguageToggle } from "../components/LanguageToggle";
import { CarnivalStatusBadge } from "../components/CarnivalStatusBadge";
import { CarnivalHeroDecor } from "../components/CarnivalDecor";
import { CarnivalEventActions } from "../components/CarnivalEventActions";
import { ShareButton } from "../components/ShareButton";
import { RectangleAd } from "../components/AdSlot";
import {
  CARNIVAL,
  CARNIVAL_ISLAND_LABELS,
  CARNIVAL_UI,
  CARNIVAL_LAST_UPDATED,
  MARQUEE_EVENTS,
  MARQUEE_DEFAULT_IMAGE,
  CARNIVAL_MARQUEE_UI,
  formatCarnivalDate,
} from "../lib/carnivalMeta";
import { useSeoTags } from "../lib/useSeoTags";

const SITE = "https://islandsdaily.com";

const readStoredLang = () => {
  try {
    const s = localStorage.getItem("ir_lang");
    if (s === "en" || s === "nl" || s === "es" || s === "pap" || s === "pap_aw") return s;
  } catch (_e) { /* ignore private-mode */ }
  return "en";
};

const ISLAND_COUNTRY_CODE = {
  aruba: "AW",
  curacao: "CW",
  bonaire: "BQ",
  stmaarten: "SX",
};

// Currency: ANG for Sint Maarten (as of Feb 2026 still ANG; Caribbean
// Guilder switchover is pending), AWG for Aruba, USD for Bonaire (BES
// municipality), ANG for Curaçao until the Caribbean Guilder rolls.
// For a free event this is cosmetic — Google's Rich Results Test
// requires *a* valid ISO currency, doesn't validate the amount.
const ISLAND_CURRENCY = {
  aruba: "AWG",
  curacao: "ANG",
  bonaire: "USD",
  stmaarten: "ANG",
};

// Build the full standalone Event JSON-LD per the Google Event Rich
// Results reference — but ONLY for events with `status: "confirmed"`.
// Estimated events (date inferred from the Lent calendar rather than
// published by SMAC/FDKK) MUST NOT emit Event schema, because doing so
// tells Google "this event is scheduled" while the visible page copy
// says "pending official confirmation from the organizer" — a
// structured-data-vs-content mismatch that Google Search Console
// flags as a domain-wide quality issue. This mirrors the same
// `status === "confirmed"` filter that CarnivalIslandPage.jsx uses
// on its Festival→subEvent graph. Estimated marquee pages still emit
// FAQPage + BreadcrumbList; they just skip Event.
//
// Once SMAC/FDKK publish their 2027 schedules (typically Oct/Nov of
// the prior year), flipping the event's `status` to `confirmed` in
// carnivalMeta.js automatically re-enables Event schema on that
// marquee page — no code change needed here.
const buildEventSchema = (marquee, lang) => {
  const parent = CARNIVAL[marquee.islandKey];
  if (!parent) return null;
  const evt = parent.events.find((e) => e.key === marquee.eventKey);
  if (!evt || !evt.date) return null; // Guarded upstream — no date, no page.
  if (evt.status !== "confirmed") return null; // Estimated → no Event schema.

  const meta = marquee.meta[lang] || marquee.meta.en;
  const description = meta.description;
  const canonical = `${SITE}/carnival/${marquee.slug}`;
  const startISO = `${evt.date}T${evt.time_local || "12:00"}:00${parent.timezone}`;

  return {
    "@context": "https://schema.org",
    "@type": "Event",
    name: meta.h1,
    description,
    startDate: startISO,
    // No endDate — parade end times aren't published by the orgs.
    // Google is happy with startDate-only Events.
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    location: {
      "@type": "Place",
      name: evt.location?.name || CARNIVAL_ISLAND_LABELS[marquee.islandKey],
      address: {
        "@type": "PostalAddress",
        addressLocality: evt.location?.city || CARNIVAL_ISLAND_LABELS[marquee.islandKey],
        addressCountry: ISLAND_COUNTRY_CODE[marquee.islandKey],
      },
    },
    organizer: {
      "@type": "Organization",
      name: parent.organizer.name,
      url: parent.organizer.url,
    },
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: ISLAND_CURRENCY[marquee.islandKey],
      availability: "https://schema.org/InStock",
      url: canonical,
      validFrom: `${parent.season.start}T00:00:00${parent.timezone}`,
    },
    image: [MARQUEE_DEFAULT_IMAGE],
    sameAs: parent.sameAs,
    url: canonical,
    dateModified: CARNIVAL_LAST_UPDATED,
    inLanguage: lang === "pap_aw" ? "pap" : lang,
  };
};

const buildFaqSchema = (marquee, lang) => {
  if (!marquee.faq?.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    inLanguage: lang === "pap_aw" ? "pap" : lang,
    mainEntity: marquee.faq.map((f) => ({
      "@type": "Question",
      name: f.q[lang] || f.q.en,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.a[lang] || f.a.en,
      },
    })),
  };
};

const buildBreadcrumbSchema = (marquee, lang) => {
  const meta = marquee.meta[lang] || marquee.meta.en;
  const islandLabel = CARNIVAL_ISLAND_LABELS[marquee.islandKey];
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
      { "@type": "ListItem", position: 2, name: "Carnival", item: `${SITE}/carnival` },
      { "@type": "ListItem", position: 3, name: `${islandLabel} Carnival 2027`, item: `${SITE}/${marquee.islandKey}/carnival` },
      { "@type": "ListItem", position: 4, name: meta.h1, item: `${SITE}/carnival/${marquee.slug}` },
    ],
  };
};

export default function CarnivalMarqueePage() {
  const { eventSlug } = useParams();
  const marquee = MARQUEE_EVENTS[eventSlug] || null;
  const [lang, setLang] = useState(() => readStoredLang());
  const [sponsorAd, setSponsorAd] = useState(null);

  useEffect(() => {
    if (!marquee?.islandKey) return;
    const backend = process.env.REACT_APP_BACKEND_URL;
    if (!backend) return;
    const ctrl = new AbortController();
    fetch(`${backend}/api/ads/carnival?page_type=carnival_marquee&island=${marquee.islandKey}`, {
      signal: ctrl.signal,
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.ad) {
          setSponsorAd(data.ad);
          fetch(`${backend}/api/ads/track/impression`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              slot: "carnival_marquee_rectangle",
              page_type: "carnival_marquee",
              island: marquee.islandKey,
              sponsor_id: data.ad.sponsor_id || "",
              sponsor_name: data.ad.sponsor_name || "",
            }),
          }).catch(() => {});
        }
      })
      .catch(() => {});
    return () => ctrl.abort();
  }, [marquee?.islandKey]);

  useEffect(() => {
    const onLangChange = () => setLang(readStoredLang());
    window.addEventListener("ir_lang_changed", onLangChange);
    window.addEventListener("storage", onLangChange);
    return () => {
      window.removeEventListener("ir_lang_changed", onLangChange);
      window.removeEventListener("storage", onLangChange);
    };
  }, []);
  const handleLangChange = useCallback((next) => {
    try {
      localStorage.setItem("ir_lang", next);
      window.dispatchEvent(new Event("ir_lang_changed"));
    } catch (_e) { /* ignore */ }
    setLang(next);
  }, []);

  const seoConfigs = useMemo(() => {
    if (!marquee) return {};
    const canonical = `${SITE}/carnival/${marquee.slug}`;
    const alternates = [
      { hreflang: "en",        href: canonical },
      { hreflang: "nl",        href: canonical },
      { hreflang: "es",        href: canonical },
      { hreflang: "pap",       href: canonical },
      { hreflang: "x-default", href: canonical },
    ];
    return {
      en:     { ...marquee.meta.en,     canonical, alternates },
      nl:     { ...marquee.meta.nl,     canonical, alternates },
      es:     { ...marquee.meta.es,     canonical, alternates },
      pap:    { ...(marquee.meta.pap    || marquee.meta.en), canonical, alternates },
      pap_aw: { ...(marquee.meta.pap_aw || marquee.meta.en), canonical, alternates },
    };
  }, [marquee]);
  useSeoTags(lang, seoConfigs);

  if (!marquee) {
    return (
      <div className="min-h-screen bg-white p-8" data-testid="carnival-marquee-404">
        <p>
          {CARNIVAL_UI.unknown_island[lang] || CARNIVAL_UI.unknown_island.en}{" "}
          <Link to="/carnival" className="text-[#0AADA0]">
            ← {CARNIVAL_UI.all_carnivals_link[lang] || CARNIVAL_UI.all_carnivals_link.en}
          </Link>
        </p>
      </div>
    );
  }

  const parent = CARNIVAL[marquee.islandKey];
  const evt = parent.events.find((e) => e.key === marquee.eventKey);
  const meta = marquee.meta[lang] || marquee.meta.en;
  const eventSchema = buildEventSchema(marquee, lang);
  const faqSchema = buildFaqSchema(marquee, lang);
  const breadcrumbSchema = buildBreadcrumbSchema(marquee, lang);

  const t = (block) => (block[lang] || block.en);
  const eventName = t(evt.labels);
  const eventAbout = t(evt.about);
  const displayDate = evt.date ? formatCarnivalDate(evt.date, lang) : "";
  const islandLabel = CARNIVAL_ISLAND_LABELS[marquee.islandKey];

  return (
    <div className="min-h-screen bg-white" data-testid={`carnival-marquee-${marquee.slug}`}>
      {eventSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(eventSchema) }}
          data-testid="carnival-marquee-event-schema"
        />
      )}
      {faqSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
          data-testid="carnival-marquee-faq-schema"
        />
      )}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
        data-testid="carnival-marquee-breadcrumb-schema"
      />

      {/* Sticky mini header — same pattern as island hub */}
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-[#EEEEEE]">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link
            to={`/${marquee.islandKey}/carnival`}
            className="inline-flex items-center gap-1.5 text-[13px] text-[#6B7C82] hover:text-[#1A2E35]"
            data-testid="carnival-marquee-back-link"
          >
            <ArrowLeft size={15} />{" "}
            {islandLabel} Carnival 2027
          </Link>
          <Link
            to="/"
            className="ml-auto font-serif-display text-[18px] leading-none"
            data-testid="carnival-marquee-home-link"
          >
            <span className="text-[#1A1A1A]">Islands</span>
            <span className="text-[#0AADA0]">Daily</span>
          </Link>
          <LanguageToggle lang={lang} onChange={handleLangChange} />
        </div>
      </div>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        {/* Phase C rollout — marquee sponsor slot at top (300x250
            rectangle sized for the narrower marquee column). */}
        <div
          className="mb-5 flex justify-center"
          data-testid="carnival-marquee-sponsor-slot"
          aria-label={`${CARNIVAL_UI.sponsored_label[lang] || CARNIVAL_UI.sponsored_label.en} placement`}
        >
          <RectangleAd
            ad={sponsorAd}
            onAdClick={() => {
              const backend = process.env.REACT_APP_BACKEND_URL;
              if (!backend || !sponsorAd) return;
              fetch(`${backend}/api/ads/track/click`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  slot: "carnival_marquee_rectangle",
                  page_type: "carnival_marquee",
                  island: marquee.islandKey,
                  sponsor_id: sponsorAd.sponsor_id || "",
                  sponsor_name: sponsorAd.sponsor_name || "",
                }),
              }).catch(() => {});
            }}
          />
        </div>

        {/* Breadcrumb trail (visible) */}
        <nav
          className="text-[11px] text-[#6B7C82] mb-4 uppercase tracking-wider"
          data-testid="carnival-marquee-breadcrumb"
        >
          <Link to="/carnival" className="hover:text-[#1A2E35]">
            Carnival
          </Link>
          {" / "}
          <Link to={`/${marquee.islandKey}/carnival`} className="hover:text-[#1A2E35]">
            {islandLabel}
          </Link>
          {" / "}
          <span className="text-[#1A2E35]">{eventName}</span>
        </nav>

        <div
          className="relative overflow-hidden rounded-2xl border border-[#F0EDE3] bg-white p-5 sm:p-6 mb-6"
          data-testid="carnival-marquee-hero"
        >
          <CarnivalHeroDecor />
          <div className="relative">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <h1
                className="font-serif-display text-3xl sm:text-4xl text-[#1A2E35] mb-3"
                data-testid="carnival-marquee-h1"
              >
                {meta.h1}
              </h1>
              <div data-testid="carnival-marquee-share">
                <ShareButton
                  title={meta.h1}
                  text={meta.description?.slice(0, 160) || meta.h1}
                  url={`https://islandsdaily.com/carnival/${eventSlug}?ref=cshare`}
                  testId="carnival-marquee-share-button"
                />
              </div>
            </div>

            {/* Fact strip — date, time, location, badge */}
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[13.5px] text-[#3D4C52] mb-2">
              {displayDate && (
                <span className="inline-flex items-center gap-1.5" data-testid="carnival-marquee-date">
                  <Calendar size={14} className="text-[#0AADA0]" />
                  {displayDate}
                </span>
              )}
              {evt.time_local && (
                <span className="inline-flex items-center gap-1.5" data-testid="carnival-marquee-time">
                  <Clock size={14} className="text-[#0AADA0]" />
                  {evt.time_local} ({parent.timezone})
                </span>
              )}
              {evt.location?.name && (
                <span className="inline-flex items-center gap-1.5" data-testid="carnival-marquee-location">
                  <MapPin size={14} className="text-[#0AADA0]" />
                  {evt.location.name}
                </span>
              )}
              <CarnivalStatusBadge
                status={evt.status}
                source={evt.status === "confirmed" ? evt.confirmed_by : null}
                lang={lang}
              />
            </div>

            {/* Add-to-calendar action on marquee — same honesty gate
                as event rows in the island hub. */}
            <div data-testid="carnival-marquee-actions">
              <CarnivalEventActions event={evt} islandKey={marquee.islandKey} lang={lang} />
            </div>
          </div>
        </div>

        {/* Estimated disclosure — visible copy that reinforces the schema
            eventStatus:EventScheduled with human-readable honesty. */}
        {evt.status === "estimated" && (
          <div
            className="text-[12.5px] text-[#856404] bg-[#FFF8E1] border border-[#FFECB3] px-3 py-2 rounded mb-6"
            data-testid="carnival-marquee-estimated-note"
          >
            <strong className="font-semibold">
              {CARNIVAL_UI.status_estimated_note[lang] || CARNIVAL_UI.status_estimated_note.en}.
            </strong>{" "}
            {CARNIVAL_MARQUEE_UI.estimated_note[lang] || CARNIVAL_MARQUEE_UI.estimated_note.en}
          </div>
        )}

        {/* Intro — reuses the event.about copy from carnivalMeta so we
            aren't rewriting from scratch. */}
        <p className="text-[15px] text-[#3D4C52] leading-[1.7] mb-8" data-testid="carnival-marquee-about">
          {eventAbout}
        </p>

        {/* About / context */}
        <Section title={CARNIVAL_MARQUEE_UI.section_context[lang] || CARNIVAL_MARQUEE_UI.section_context.en}>
          <p data-testid="carnival-marquee-context">{t(marquee.context)}</p>
        </Section>

        {/* What to expect */}
        <Section title={CARNIVAL_MARQUEE_UI.section_what_to_expect[lang] || CARNIVAL_MARQUEE_UI.section_what_to_expect.en}>
          <p data-testid="carnival-marquee-what-to-expect">{t(marquee.what_to_expect)}</p>
        </Section>

        {/* Visitor info */}
        <Section title={CARNIVAL_MARQUEE_UI.section_visitor_info[lang] || CARNIVAL_MARQUEE_UI.section_visitor_info.en}>
          <p data-testid="carnival-marquee-visitor-info">{t(marquee.visitor_info)}</p>
        </Section>

        {/* Organizer */}
        <div className="text-[13px] text-[#3D4C52] mb-10" data-testid="carnival-marquee-organizer">
          <span className="uppercase tracking-wider text-[10px] text-[#6B7C82] mr-2">
            {CARNIVAL_UI.section_organizer[lang] || CARNIVAL_UI.section_organizer.en}:
          </span>
          <a
            href={parent.organizer.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#0AADA0] hover:underline"
          >
            {parent.organizer.name}
          </a>
        </div>

        {/* FAQ */}
        {marquee.faq?.length ? (
          <section className="border-t border-[#EEEEEE] pt-8 mb-10" data-testid="carnival-marquee-faq">
            <h2 className="font-serif-display text-xl sm:text-2xl text-[#1A2E35] mb-4">
              {CARNIVAL_UI.section_faq[lang] || CARNIVAL_UI.section_faq.en}
            </h2>
            <div className="flex flex-col gap-5">
              {marquee.faq.map((f, i) => (
                <div key={i} data-testid={`carnival-marquee-faq-item-${i}`}>
                  <div className="text-[15px] font-medium text-[#1A2E35] mb-1.5">
                    {t(f.q)}
                  </div>
                  <div className="text-[14px] text-[#3D4C52] leading-[1.7]">
                    {t(f.a)}
                  </div>
                </div>
              ))}
            </div>
          </section>
        ) : null}

        {/* Cross-links back to island hub + comparison hub. Both are
            required per the Session 2 spec. */}
        <nav
          className="border-t border-[#EEEEEE] pt-8 flex flex-col sm:flex-row gap-3 text-[13.5px]"
          data-testid="carnival-marquee-crosslinks"
        >
          <Link
            to={`/${marquee.islandKey}/carnival`}
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#E5E7EB] rounded-md hover:bg-[#F9FAFB] text-[#1A2E35]"
            data-testid="carnival-marquee-link-island-hub"
          >
            ← {CARNIVAL_MARQUEE_UI.view_island_hub[lang] || CARNIVAL_MARQUEE_UI.view_island_hub.en}
          </Link>
          <Link
            to="/carnival"
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#E5E7EB] rounded-md hover:bg-[#F9FAFB] text-[#1A2E35]"
            data-testid="carnival-marquee-link-hub"
          >
            {CARNIVAL_MARQUEE_UI.view_all_carnivals[lang] || CARNIVAL_MARQUEE_UI.view_all_carnivals.en} →
          </Link>
        </nav>

        <div className="text-[11px] text-[#6B7C82] mt-8" data-testid="carnival-marquee-last-updated">
          {CARNIVAL_UI.last_updated[lang] || CARNIVAL_UI.last_updated.en}: {CARNIVAL_LAST_UPDATED}
        </div>
      </main>

      <Footer />
    </div>
  );
}

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-[11px] uppercase tracking-wider text-[#0AADA0] font-semibold mb-2">
      {title}
    </h2>
    <div className="text-[14.5px] text-[#3D4C52] leading-[1.7]">{children}</div>
  </section>
);
